/**
 * MindCare Maintenance Engine
 * P3 - Alışkanlık Sürekliliği ve Ritüel Koruma Motoru
 * 
 * Görevler:
 * - Günlük bakım kontrolü
 * - Haftalık ritim takibi
 * - Süreklilik koruma mekanizmaları (kalkan, tampon, mola)
 * - Motivasyon yönetimi
 * - Recovery ritüelleri
 * - Yeni kullanıcı desteği
 */

import { 
  rhieEventBus, 
  RHIE_EVENTS, 
  STREAK_SHIELD_CONFIG,
  BUFFER_DAYS_CONFIG,
  GRACE_MODE_CONFIG,
  WEEKLY_RHYTHM_CONFIG,
  RECOVERY_RITUAL_CONFIG
} from './zihinKilavuzuEventBus';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==========================================
// MAINTENANCE STATE INTERFACE
// ==========================================

const createInitialMaintenanceState = () => ({
  // Streak Shield
  streakShield: {
    available: 0,
    usedThisMonth: 0,
    lastUsedDate: null,
    cooldownUntil: null,
    isActive: false
  },
  
  // Buffer Days
  bufferDays: {
    available: BUFFER_DAYS_CONFIG.initialBufferDays,
    earned: 0,
    used: 0,
    lastEarnedDate: null
  },
  
  // Grace Mode
  graceMode: {
    isActive: false,
    startDate: null,
    endDate: null,
    reason: null,
    streakFrozenAt: 0
  },
  
  // Weekly Rhythm
  weeklyRhythm: {
    currentWeekDays: 0,
    targetDays: WEEKLY_RHYTHM_CONFIG.targetDaysPerWeek,
    completedDates: [],
    weekStartDate: null,
    consistency: 0, // 0-100%
    lastWeekScore: 0
  },
  
  // Recovery State
  recovery: {
    isInRecoveryMode: false,
    daysSinceLastActivity: 0,
    suggestedRitual: null,
    recoveryWindowEnds: null
  },
  
  // Motivation
  motivation: {
    score: 100,
    trend: 'stable', // 'rising', 'falling', 'stable'
    lastBoostDate: null,
    lowMotivationStreak: 0
  },
  
  // New User State
  newUser: {
    isNewUser: false,
    joinDate: null,
    firstRitualDate: null,
    firstStreakDate: null,
    graduatedDate: null,
    onboardingProgress: 0
  },
  
  // Maintenance History
  history: {
    lastCheckDate: null,
    checksThisWeek: 0,
    actionsThisWeek: []
  }
});

// ==========================================
// P3 REHBER MESAJLARI
// ==========================================

const MAINTENANCE_GUIDE_MESSAGES = {
  // Süreklilik Kalkanı
  [RHIE_EVENTS.STREAK_SHIELD_AVAILABLE]: {
    Farkındalık: { 
      title: 'Süreklilik kalkanı hazır ◇', 
      body: 'Bugün ritüelini tamamlayamazsan, kalkanını kullanabilirsin.',
      duration: 6000 
    },
    Uyanış: { title: 'Kalkan hazır', body: 'Koruma mevcut.', duration: 4000 },
    Dönüşüm: null,
    Ustalık: null,
    Mentor: null
  },
  [RHIE_EVENTS.STREAK_SHIELD_USED]: {
    Farkındalık: { 
      title: 'Kalkan kullanıldı ◇', 
      body: 'Sürekliliğin korundu. Hazırsan, yarın devam edebilirsin.',
      duration: 5000 
    },
    Uyanış: { title: 'Korundu', body: 'Süreklilik aktif.', duration: 3000 },
    Dönüşüm: { title: '◇', body: null, duration: 2000 },
    Ustalık: null,
    Mentor: null
  },
  
  // Tampon Günler
  [RHIE_EVENTS.BUFFER_DAY_GRANTED]: {
    Farkındalık: { 
      title: 'Tampon Gün Kazandın ∗', 
      body: 'Tutarlılığın için Zihin Puanı kazancı! Bu günü zor zamanlarda kullanabilirsin.',
      duration: 5000 
    },
    Uyanış: { title: '+1 Tampon', body: 'Kazancın hazır.', duration: 3000 },
    Dönüşüm: null,
    Ustalık: null,
    Mentor: null
  },
  [RHIE_EVENTS.BUFFER_DAY_USED]: {
    Farkındalık: { 
      title: 'Tampon Gün Kullanıldı ∗', 
      body: 'Sürekliliğin korunuyor. Kendine zaman tanı.',
      duration: 5000 
    },
    Uyanış: { title: 'Tampon Aktif', body: 'Koruma sağlandı.', duration: 3000 },
    Dönüşüm: null,
    Ustalık: null,
    Mentor: null
  },
  
  // Grace Mode
  [RHIE_EVENTS.GRACE_MODE_ACTIVATED]: {
    Farkındalık: { 
      title: 'Mola Modu Aktif ∗', 
      body: 'Kendine iyi bak. Bu hata değil… mola. Hazır olduğunda buradayız.',
      duration: 6000 
    },
    Uyanış: { title: 'Mola Başladı', body: 'Ritüel sürekliliği korunuyor.', duration: 4000 },
    Dönüşüm: { title: 'Mola', body: null, duration: 2000 },
    Ustalık: null,
    Mentor: null
  },
  [RHIE_EVENTS.GRACE_WELCOME_BACK]: {
    Farkındalık: { 
      title: 'Hoş Geldin ✧', 
      body: 'Molan sona erdi. Aynı yere değil… daha bilinçli geri döndün.',
      duration: 7000 
    },
    Uyanış: { title: 'Hoşgeldin', body: 'Yolculuğunu sürdür.', duration: 4000 },
    Dönüşüm: { title: '◇', body: null, duration: 2000 },
    Ustalık: null,
    Mentor: null
  },
  
  // Recovery
  [RHIE_EVENTS.RECOVERY_RITUAL_SUGGESTED]: {
    Farkındalık: { 
      title: 'Kısa bir başlangıç ∗', 
      body: 'Derin nefes al… ve başla. Bu ritüel… senden sadece birkaç dakika ister.',
      duration: 6000 
    },
    Uyanış: { title: 'Mini Ritüel', body: '5 dakika yeter.', duration: 4000 },
    Dönüşüm: { title: 'Kendine izin ver', body: null, duration: 2000 },
    Ustalık: null,
    Mentor: null
  },
  [RHIE_EVENTS.RECOVERY_RITUAL_COMPLETE]: {
    Farkındalık: { 
      title: 'Geri Döndün ✧', 
      body: 'Bu bir kayıp değil… dönüşün başlangıcı. Bonus Zihin Puanı kazandın.',
      duration: 6000 
    },
    Uyanış: { title: 'Döndün.', body: 'Bonus ZP.', duration: 4000 },
    Dönüşüm: { title: '✧', body: null, duration: 2000 },
    Ustalık: null,
    Mentor: null
  },
  
  // Weekly Rhythm
  [RHIE_EVENTS.WEEKLY_REPORT_READY]: {
    Farkındalık: { 
      title: 'Haftalık özet ◈', 
      body: (data) => `Bu hafta ${data.completedDays}/7 gün tamamladın. Devam ettiğinde… farkı göreceksin.`,
      duration: 6000 
    },
    Uyanış: { title: 'Hafta Özeti', body: (data) => `${data.completedDays} gün`, duration: 4000 },
    Dönüşüm: null,
    Ustalık: null,
    Mentor: null
  },
  [RHIE_EVENTS.WEEKLY_GOAL_ACHIEVED]: {
    Farkındalık: { 
      title: 'Haftalık hedef tamam ✦', 
      body: 'Bir yılı yatır… bir geleceği kazan. Mükemmel hafta bonusu kazandın.',
      duration: 6000 
    },
    Uyanış: { title: 'Hedef Tamam!', body: 'Bonus ZP.', duration: 4000 },
    Dönüşüm: { title: '✦', body: null, duration: 2000 },
    Ustalık: null,
    Mentor: null
  },
  
  // New User
  [RHIE_EVENTS.NEW_USER_FIRST_RITUAL]: {
    Farkındalık: { 
      title: 'İlk adım ✧', 
      body: 'Kapının eşiğindesin… bir adım sonra… Lonca. İlk ritüelini tamamladın.',
      duration: 7000 
    },
    Uyanış: { title: 'İlk Ritüel.', body: 'Başardın.', duration: 4000 },
    Dönüşüm: null,
    Ustalık: null,
    Mentor: null
  },
  [RHIE_EVENTS.NEW_USER_FIRST_STREAK]: {
    Farkındalık: { 
      title: 'İlk süreklilik ◇', 
      body: 'Art arda 2 gün. Ritüel sürekliliği oluşturmaya başladın.',
      duration: 6000 
    },
    Uyanış: { title: 'Süreklilik Başladı.', body: '2 gün.', duration: 4000 },
    Dönüşüm: null,
    Ustalık: null,
    Mentor: null
  },
  [RHIE_EVENTS.NEW_USER_GRADUATED]: {
    Farkındalık: { 
      title: 'Artık deneyimlisin ✦', 
      body: '7 günü tamamladın. Usta olmak… bildiklerini değil… bildiklerinden ötesini taşımaktır.',
      duration: 7000 
    },
    Uyanış: { title: 'Dönüşüm Başladı.', body: 'Tebrikler.', duration: 4000 },
    Dönüşüm: null,
    Ustalık: null,
    Mentor: null
  },
  
  // Motivation
  [RHIE_EVENTS.MOTIVATION_LOW]: {
    Farkındalık: { 
      title: 'Bugün zor mu? ◇', 
      body: 'Bu düşüş değil… yön değişimi. Kısa bir nefes egzersizi ile başla.',
      duration: 6000 
    },
    Uyanış: { title: 'Hafif Başla', body: 'Küçük adım.', duration: 4000 },
    Dönüşüm: null,
    Ustalık: null,
    Mentor: null
  },
  [RHIE_EVENTS.MOTIVATION_RESTORED]: {
    Farkındalık: { 
      title: 'Enerjin geri geldi ◈', 
      body: 'Harika. Motivasyonun yükseldi. Yolculuğunu sürdür.',
      duration: 5000 
    },
    Uyanış: { title: 'Enerji.', body: 'Hazırım.', duration: 3000 },
    Dönüşüm: null,
    Ustalık: null,
    Mentor: null
  }
};

// ==========================================
// MINDCARE MAINTENANCE ENGINE CLASS
// ==========================================

class MindCareMaintenanceEngine {
  constructor() {
    this.state = createInitialMaintenanceState();
    this.token = null;
    this.user = null;
    this.userLevel = 'Farkındalık';
    this.initialized = false;
    this.rhieEngine = null; // Reference to main RHIE engine
  }
  
  // ==========================================
  // INITIALIZATION
  // ==========================================
  
  async initialize(token, user, rhieEngine) {
    this.token = token;
    this.user = user;
    this.userLevel = user?.rank || 'Farkındalık';
    this.rhieEngine = rhieEngine;
    
    console.log('[MindCare] Initializing Maintenance Engine...');
    
    // Load persisted state from backend/localStorage
    await this.loadState();
    
    // Detect new user
    this.detectNewUser();
    
    // Run initial maintenance check
    await this.runDailyMaintenance();
    
    // Check weekly rhythm
    this.checkWeeklyRhythm();
    
    this.initialized = true;
    console.log('[MindCare] Maintenance Engine initialized', this.state);
    
    return this;
  }
  
  async loadState() {
    // Try to load from localStorage first
    try {
      const savedState = localStorage.getItem('mindcare_state');
      if (savedState) {
        const parsed = JSON.parse(savedState);
        this.state = { ...createInitialMaintenanceState(), ...parsed };
      }
    } catch (e) {
      console.warn('[MindCare] Could not load saved state:', e);
    }
    
    // Fetch from backend if available
    if (this.token) {
      try {
        const response = await fetch(`${API_URL}/api/user/maintenance-state`, {
          headers: { 'Authorization': `Bearer ${this.token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            this.state = { ...this.state, ...data.data };
          }
        }
      } catch (e) {
        // Backend endpoint may not exist yet, use local state
        console.log('[MindCare] Using local state (backend not available)');
      }
    }
  }
  
  saveState() {
    try {
      localStorage.setItem('mindcare_state', JSON.stringify(this.state));
    } catch (e) {
      console.warn('[MindCare] Could not save state:', e);
    }
  }
  
  // ==========================================
  // GUIDE MESSAGE SYSTEM
  // ==========================================
  
  getGuideMessage(event, data = {}) {
    const messages = MAINTENANCE_GUIDE_MESSAGES[event];
    if (!messages) return null;
    
    const message = messages[this.userLevel];
    if (!message) return null;
    
    let body = message.body;
    if (typeof body === 'function') {
      body = body(data);
    }
    
    return {
      title: message.title,
      body: body,
      duration: message.duration || 4000,
      event: event
    };
  }
  
  emitWithGuide(event, data = {}) {
    rhieEventBus.emit(event, data);
    
    const guideMessage = this.getGuideMessage(event, data);
    if (guideMessage) {
      rhieEventBus.emit(RHIE_EVENTS.HINT_SHOWN, {
        hint: {
          id: `mindcare_${event}_${Date.now()}`,
          ...guideMessage,
          behavior: { autoDismissMs: guideMessage.duration },
          position: 'top-center'
        }
      });
    }
  }
  
  // ==========================================
  // DAILY MAINTENANCE
  // ==========================================
  
  async runDailyMaintenance() {
    const today = new Date().toDateString();
    
    // Skip if already checked today
    if (this.state.history.lastCheckDate === today) {
      console.log('[MindCare] Daily maintenance already completed');
      return;
    }
    
    console.log('[MindCare] Running daily maintenance...');
    
    // 1. Check streak status
    this.checkStreakStatus();
    
    // 2. Check buffer days earning
    this.checkBufferDaysEarning();
    
    // 3. Check grace mode status
    this.checkGraceModeStatus();
    
    // 4. Calculate motivation score
    this.calculateMotivation();
    
    // 5. Check for recovery needs
    this.checkRecoveryNeeds();
    
    // 6. Check shield availability
    this.checkShieldAvailability();
    
    // Update history
    this.state.history.lastCheckDate = today;
    this.state.history.checksThisWeek++;
    
    this.saveState();
    
    this.emitWithGuide(RHIE_EVENTS.MAINTENANCE_CHECK_COMPLETE, {
      date: today,
      actions: this.state.history.actionsThisWeek
    });
  }
  
  // ==========================================
  // STREAK SHIELD SYSTEM
  // ==========================================
  
  checkShieldAvailability() {
    const now = new Date();
    const currentMonth = now.getMonth();
    
    // Reset monthly shields if new month
    const lastUsed = this.state.streakShield.lastUsedDate 
      ? new Date(this.state.streakShield.lastUsedDate).getMonth() 
      : -1;
    
    if (lastUsed !== currentMonth) {
      this.state.streakShield.usedThisMonth = 0;
    }
    
    // Calculate available shields
    const maxShields = STREAK_SHIELD_CONFIG.maxShieldsPerMonth + 
      (this.user?.is_vip ? STREAK_SHIELD_CONFIG.vipBonusShields : 0);
    
    this.state.streakShield.available = maxShields - this.state.streakShield.usedThisMonth;
    
    // Check cooldown
    if (this.state.streakShield.cooldownUntil) {
      const cooldownEnd = new Date(this.state.streakShield.cooldownUntil);
      if (now < cooldownEnd) {
        this.state.streakShield.available = 0;
      } else {
        this.state.streakShield.cooldownUntil = null;
      }
    }
    
    if (this.state.streakShield.available > 0) {
      this.emitWithGuide(RHIE_EVENTS.STREAK_SHIELD_AVAILABLE, {
        available: this.state.streakShield.available
      });
    }
  }
  
  activateStreakShield() {
    if (this.state.streakShield.available <= 0) {
      return { success: false, message: 'Kalkan mevcut değil' };
    }
    
    if (this.state.graceMode.isActive) {
      return { success: false, message: 'Mola modunda kalkan kullanılamaz' };
    }
    
    // Use shield
    this.state.streakShield.available--;
    this.state.streakShield.usedThisMonth++;
    this.state.streakShield.lastUsedDate = new Date().toISOString();
    this.state.streakShield.isActive = true;
    
    // Set cooldown
    const cooldownEnd = new Date();
    cooldownEnd.setDate(cooldownEnd.getDate() + STREAK_SHIELD_CONFIG.cooldownDays);
    this.state.streakShield.cooldownUntil = cooldownEnd.toISOString();
    
    this.saveState();
    
    this.emitWithGuide(RHIE_EVENTS.STREAK_SHIELD_USED, {
      remaining: this.state.streakShield.available
    });
    
    return { success: true, remaining: this.state.streakShield.available };
  }
  
  deactivateShield() {
    this.state.streakShield.isActive = false;
    this.saveState();
  }
  
  isStreakProtected() {
    return this.state.streakShield.isActive || 
           this.state.graceMode.isActive || 
           this.state.bufferDays.available > 0;
  }
  
  // ==========================================
  // BUFFER DAYS SYSTEM
  // ==========================================
  
  checkBufferDaysEarning() {
    const now = new Date();
    const lastEarned = this.state.bufferDays.lastEarnedDate 
      ? new Date(this.state.bufferDays.lastEarnedDate) 
      : null;
    
    // Earn buffer days based on consistency (every 2 weeks of 5+ day activity)
    if (!lastEarned || (now - lastEarned) >= 14 * 24 * 60 * 60 * 1000) {
      // Check if user was consistent
      if (this.state.weeklyRhythm.consistency >= 70) {
        this.grantBufferDay();
      }
    }
  }
  
  grantBufferDay() {
    if (this.state.bufferDays.available >= BUFFER_DAYS_CONFIG.maxBuffer) {
      return; // Already at max
    }
    
    this.state.bufferDays.available++;
    this.state.bufferDays.earned++;
    this.state.bufferDays.lastEarnedDate = new Date().toISOString();
    
    this.saveState();
    
    this.emitWithGuide(RHIE_EVENTS.BUFFER_DAY_GRANTED, {
      available: this.state.bufferDays.available
    });
  }
  
  useBufferDay() {
    if (this.state.bufferDays.available <= 0) {
      return { success: false, message: 'Buffer gün mevcut değil' };
    }
    
    this.state.bufferDays.available--;
    this.state.bufferDays.used++;
    
    this.saveState();
    
    this.emitWithGuide(RHIE_EVENTS.BUFFER_DAY_USED, {
      remaining: this.state.bufferDays.available
    });
    
    return { success: true, remaining: this.state.bufferDays.available };
  }
  
  // ==========================================
  // GRACE MODE (Tatil/Sağlık)
  // ==========================================
  
  activateGraceMode(days, reason = 'diğer') {
    if (days < GRACE_MODE_CONFIG.minDays || days > GRACE_MODE_CONFIG.maxDays) {
      return { 
        success: false, 
        message: `Mola süresi ${GRACE_MODE_CONFIG.minDays}-${GRACE_MODE_CONFIG.maxDays} gün arası olmalı` 
      };
    }
    
    const startDate = new Date();
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + days);
    
    // Freeze current streak
    const currentStreak = this.rhieEngine?.getCurrentStreak() || 0;
    
    this.state.graceMode = {
      isActive: true,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      reason: reason,
      streakFrozenAt: currentStreak
    };
    
    this.saveState();
    
    rhieEventBus.emit(RHIE_EVENTS.GRACE_MODE_REQUESTED, { days, reason });
    this.emitWithGuide(RHIE_EVENTS.GRACE_MODE_ACTIVATED, { 
      days, 
      reason,
      endDate: endDate.toLocaleDateString('tr-TR')
    });
    
    return { 
      success: true, 
      endDate: endDate.toISOString(),
      streakFrozen: currentStreak
    };
  }
  
  checkGraceModeStatus() {
    if (!this.state.graceMode.isActive) return;
    
    const now = new Date();
    const endDate = new Date(this.state.graceMode.endDate);
    
    if (now >= endDate) {
      this.endGraceMode();
    }
  }
  
  endGraceMode() {
    const frozenStreak = this.state.graceMode.streakFrozenAt;
    
    this.state.graceMode = {
      isActive: false,
      startDate: null,
      endDate: null,
      reason: null,
      streakFrozenAt: 0
    };
    
    this.saveState();
    
    rhieEventBus.emit(RHIE_EVENTS.GRACE_MODE_ENDED, { 
      restoredStreak: frozenStreak 
    });
    this.emitWithGuide(RHIE_EVENTS.GRACE_WELCOME_BACK, {
      streak: frozenStreak
    });
    
    return { success: true, restoredStreak: frozenStreak };
  }
  
  // ==========================================
  // WEEKLY RHYTHM TRACKING
  // ==========================================
  
  checkWeeklyRhythm() {
    const now = new Date();
    const dayOfWeek = now.getDay();
    
    // Calculate week start (Monday)
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - ((dayOfWeek + 6) % 7));
    weekStart.setHours(0, 0, 0, 0);
    
    const weekStartStr = weekStart.toDateString();
    
    // Reset if new week
    if (this.state.weeklyRhythm.weekStartDate !== weekStartStr) {
      // Store last week's score
      this.state.weeklyRhythm.lastWeekScore = this.state.weeklyRhythm.currentWeekDays;
      
      // Generate weekly report for previous week
      if (this.state.weeklyRhythm.weekStartDate) {
        this.generateWeeklyReport();
      }
      
      // Reset for new week
      this.state.weeklyRhythm.weekStartDate = weekStartStr;
      this.state.weeklyRhythm.currentWeekDays = 0;
      this.state.weeklyRhythm.completedDates = [];
    }
    
    // Calculate consistency
    this.calculateConsistency();
  }
  
  recordDailyCompletion() {
    const today = new Date().toDateString();
    
    if (!this.state.weeklyRhythm.completedDates.includes(today)) {
      this.state.weeklyRhythm.completedDates.push(today);
      this.state.weeklyRhythm.currentWeekDays++;
      
      // Check if weekly goal achieved
      if (this.state.weeklyRhythm.currentWeekDays >= this.state.weeklyRhythm.targetDays) {
        this.emitWithGuide(RHIE_EVENTS.WEEKLY_GOAL_ACHIEVED, {
          completedDays: this.state.weeklyRhythm.currentWeekDays
        });
        
        // Award bonus XP
        if (this.rhieEngine) {
          this.rhieEngine.awardXP(
            WEEKLY_RHYTHM_CONFIG.perfectWeekBonus, 
            'weekly_goal'
          );
        }
      }
    }
    
    this.calculateConsistency();
    this.saveState();
  }
  
  calculateConsistency() {
    // Calculate 4-week rolling consistency
    const completedLastWeek = this.state.weeklyRhythm.lastWeekScore;
    const completedThisWeek = this.state.weeklyRhythm.currentWeekDays;
    const targetPerWeek = this.state.weeklyRhythm.targetDays;
    
    const consistency = Math.round(
      ((completedLastWeek + completedThisWeek) / (targetPerWeek * 2)) * 100
    );
    
    this.state.weeklyRhythm.consistency = Math.min(100, consistency);
  }
  
  generateWeeklyReport() {
    const report = {
      completedDays: this.state.weeklyRhythm.lastWeekScore,
      targetDays: this.state.weeklyRhythm.targetDays,
      consistency: this.state.weeklyRhythm.consistency,
      goalAchieved: this.state.weeklyRhythm.lastWeekScore >= this.state.weeklyRhythm.targetDays,
      dates: [...this.state.weeklyRhythm.completedDates]
    };
    
    this.emitWithGuide(RHIE_EVENTS.WEEKLY_REPORT_READY, report);
    
    return report;
  }
  
  // ==========================================
  // RECOVERY SYSTEM
  // ==========================================
  
  checkRecoveryNeeds() {
    const daysSinceActivity = this.rhieEngine?.getDaysSinceLastActivity() || 0;
    
    this.state.recovery.daysSinceLastActivity = daysSinceActivity;
    
    // Trigger recovery mode based on inactivity
    if (daysSinceActivity >= 3 && !this.state.graceMode.isActive) {
      this.enterRecoveryMode(daysSinceActivity);
    }
  }
  
  enterRecoveryMode(daysSince) {
    this.state.recovery.isInRecoveryMode = true;
    
    // Calculate recovery window (48 hours from now)
    const recoveryWindowEnd = new Date();
    recoveryWindowEnd.setHours(recoveryWindowEnd.getHours() + RECOVERY_RITUAL_CONFIG.streakRecoveryWindow);
    this.state.recovery.recoveryWindowEnds = recoveryWindowEnd.toISOString();
    
    // Get a short recovery ritual suggestion
    const suggestion = this.getSuggestedRecoveryRitual();
    this.state.recovery.suggestedRitual = suggestion;
    
    this.saveState();
    
    this.emitWithGuide(RHIE_EVENTS.RECOVERY_RITUAL_SUGGESTED, {
      daysSince,
      ritual: suggestion
    });
  }
  
  getSuggestedRecoveryRitual() {
    // Return a short, easy session
    const recommendations = this.rhieEngine?.getPersonalizedRecommendations() || [];
    
    // Filter for short sessions (5 min or less)
    const shortSessions = recommendations.filter(
      r => (r.session?.duration_seconds || 600) <= RECOVERY_RITUAL_CONFIG.maxDurationMinutes * 60
    );
    
    return shortSessions[0]?.session || {
      id: 'recovery_breath',
      title: '5 Dakika Nefes Ritüeli',
      duration_seconds: 300,
      base_xp: 10,
      isRecoveryRitual: true
    };
  }
  
  completeRecoveryRitual(session) {
    this.state.recovery.isInRecoveryMode = false;
    this.state.recovery.suggestedRitual = null;
    
    // Check if within recovery window for streak restoration
    const now = new Date();
    const windowEnd = this.state.recovery.recoveryWindowEnds 
      ? new Date(this.state.recovery.recoveryWindowEnds) 
      : null;
    
    let streakRestored = false;
    if (windowEnd && now <= windowEnd) {
      // Restore streak (partial)
      streakRestored = true;
      rhieEventBus.emit(RHIE_EVENTS.RECOVERY_STREAK_RESTORED, {
        restoredStreak: Math.max(1, this.rhieEngine?.getCurrentStreak() || 0)
      });
    }
    
    this.saveState();
    
    this.emitWithGuide(RHIE_EVENTS.RECOVERY_RITUAL_COMPLETE, {
      xpMultiplier: RECOVERY_RITUAL_CONFIG.xpMultiplier,
      streakRestored
    });
    
    return {
      success: true,
      xpMultiplier: RECOVERY_RITUAL_CONFIG.xpMultiplier,
      streakRestored
    };
  }
  
  // ==========================================
  // MOTIVATION SYSTEM
  // ==========================================
  
  calculateMotivation() {
    let score = 100;
    
    // Factor 1: Days since activity (-10 per day, max -50)
    const daysSince = this.state.recovery.daysSinceLastActivity;
    score -= Math.min(50, daysSince * 10);
    
    // Factor 2: Broken streak (-20)
    if (this.rhieEngine?.getCurrentStreak() === 0 && daysSince > 0) {
      score -= 20;
    }
    
    // Factor 3: Weekly consistency bonus (+20)
    if (this.state.weeklyRhythm.consistency >= 70) {
      score += 20;
    }
    
    // Factor 4: Skip count penalty (-5 per skip, max -25)
    const skipCount = this.rhieEngine?.skipCount || 0;
    score -= Math.min(25, skipCount * 5);
    
    // Clamp to 0-100
    score = Math.max(0, Math.min(100, score));
    
    // Determine trend
    const previousScore = this.state.motivation.score;
    let trend = 'stable';
    if (score > previousScore + 10) trend = 'rising';
    else if (score < previousScore - 10) trend = 'falling';
    
    // Update state
    this.state.motivation.score = score;
    this.state.motivation.trend = trend;
    
    // Track low motivation streak
    if (score < 40) {
      this.state.motivation.lowMotivationStreak++;
      
      if (this.state.motivation.lowMotivationStreak >= 2) {
        this.emitWithGuide(RHIE_EVENTS.MOTIVATION_LOW, { score });
      }
    } else {
      if (this.state.motivation.lowMotivationStreak >= 2) {
        this.emitWithGuide(RHIE_EVENTS.MOTIVATION_RESTORED, { score });
      }
      this.state.motivation.lowMotivationStreak = 0;
    }
    
    this.saveState();
  }
  
  boostMotivation(reason = 'manual') {
    this.state.motivation.score = Math.min(100, this.state.motivation.score + 30);
    this.state.motivation.lastBoostDate = new Date().toISOString();
    this.state.motivation.lowMotivationStreak = 0;
    
    this.saveState();
    
    rhieEventBus.emit(RHIE_EVENTS.MOTIVATION_BOOST_NEEDED, { reason });
  }
  
  // ==========================================
  // NEW USER SUPPORT
  // ==========================================
  
  detectNewUser() {
    const joinDate = this.user?.created_at || this.user?.join_date;
    if (!joinDate) return;
    
    const join = new Date(joinDate);
    const now = new Date();
    const daysSinceJoin = Math.floor((now - join) / (1000 * 60 * 60 * 24));
    
    // Consider user "new" for first 7 days
    this.state.newUser.isNewUser = daysSinceJoin <= 7;
    this.state.newUser.joinDate = joinDate;
    
    if (this.state.newUser.isNewUser) {
      rhieEventBus.emit(RHIE_EVENTS.NEW_USER_DETECTED, { daysSinceJoin });
    }
  }
  
  recordFirstRitual() {
    if (!this.state.newUser.firstRitualDate) {
      this.state.newUser.firstRitualDate = new Date().toISOString();
      this.state.newUser.onboardingProgress = 33;
      
      this.saveState();
      
      this.emitWithGuide(RHIE_EVENTS.NEW_USER_FIRST_RITUAL, {});
    }
  }
  
  recordFirstStreak() {
    if (!this.state.newUser.firstStreakDate && this.rhieEngine?.getCurrentStreak() >= 2) {
      this.state.newUser.firstStreakDate = new Date().toISOString();
      this.state.newUser.onboardingProgress = 66;
      
      this.saveState();
      
      this.emitWithGuide(RHIE_EVENTS.NEW_USER_FIRST_STREAK, {});
    }
  }
  
  graduateNewUser() {
    if (!this.state.newUser.graduatedDate && this.rhieEngine?.getCurrentStreak() >= 7) {
      this.state.newUser.graduatedDate = new Date().toISOString();
      this.state.newUser.isNewUser = false;
      this.state.newUser.onboardingProgress = 100;
      
      this.saveState();
      
      this.emitWithGuide(RHIE_EVENTS.NEW_USER_GRADUATED, {});
      
      // Award graduation badge
      if (this.rhieEngine) {
        this.rhieEngine.awardBadge('İlk Hafta Ustası', 'first_week_master');
      }
    }
  }
  
  checkNewUserProgress() {
    if (!this.state.newUser.isNewUser) return;
    
    this.recordFirstRitual();
    this.recordFirstStreak();
    this.graduateNewUser();
  }
  
  // ==========================================
  // EDGE CASE HANDLERS
  // ==========================================
  
  handleMissedDay(daysMissed = 1) {
    console.log(`[MindCare] Handling ${daysMissed} missed day(s)`);
    
    // 1 day: Check for shield or buffer
    if (daysMissed === 1) {
      if (this.state.streakShield.available > 0) {
        return this.activateStreakShield();
      } else if (this.state.bufferDays.available > 0) {
        return this.useBufferDay();
      }
    }
    
    // 2-3 days: Suggest recovery ritual
    if (daysMissed >= 2 && daysMissed <= 3) {
      this.enterRecoveryMode(daysMissed);
      return { 
        success: true, 
        action: 'recovery_mode',
        message: 'Kısa bir ritüelle geri dönebilirsin'
      };
    }
    
    // 3+ days: Suggest grace mode or accept streak break
    if (daysMissed > 3) {
      return {
        success: false,
        action: 'streak_broken',
        message: 'Serin sıfırlandı ama yeniden başlayabilirsin!',
        suggestion: 'grace_mode'
      };
    }
    
    return { success: false, action: 'no_protection' };
  }
  
  checkStreakStatus() {
    const daysSinceActivity = this.rhieEngine?.getDaysSinceLastActivity() || 0;
    const currentStreak = this.rhieEngine?.getCurrentStreak() || 0;
    const hour = new Date().getHours();
    
    // Evening reminder (after 6 PM)
    if (hour >= 18 && daysSinceActivity === 0 && !this.rhieEngine?.isTodayCompleted()) {
      rhieEventBus.emit(RHIE_EVENTS.STREAK_AT_RISK, { currentStreak });
    }
    
    // Auto-use protection if missed day detected
    if (daysSinceActivity >= 1 && currentStreak > 0) {
      this.handleMissedDay(daysSinceActivity);
    }
  }
  
  // ==========================================
  // GETTERS
  // ==========================================
  
  getState() { return this.state; }
  getStreakShield() { return this.state.streakShield; }
  getBufferDays() { return this.state.bufferDays; }
  getGraceMode() { return this.state.graceMode; }
  getWeeklyRhythm() { return this.state.weeklyRhythm; }
  getRecoveryState() { return this.state.recovery; }
  getMotivation() { return this.state.motivation; }
  getNewUserState() { return this.state.newUser; }
  isInGraceMode() { return this.state.graceMode.isActive; }
  isInRecoveryMode() { return this.state.recovery.isInRecoveryMode; }
  getMotivationScore() { return this.state.motivation.score; }
  getWeeklyConsistency() { return this.state.weeklyRhythm.consistency; }
}

// Singleton instance
export const mindCareEngine = new MindCareMaintenanceEngine();

export default mindCareEngine;
